"use client";

import { useId, useRef } from "react";
import { Noise } from "@ui/components/core/Noise/Noise";
import textureAgents1 from "./textures/agents-1.jpg";
import imageAgents1 from "./images/agents-1.png";
import textureCreative1 from "./textures/creative-1.jpg";
import imageCreative1 from "./images/creative-1.png";
import textureCreative2 from "./textures/creative-2.jpg";
import imageCreative2 from "./images/creative-2.png";
import textureCreative3 from "./textures/creative-3.jpg";
import imageCreative3 from "./images/creative-3.png";
import textureCreative4 from "./textures/creative-4.jpg";
import imageCreative4 from "./images/creative-4.png";
import textureCreative5 from "./textures/creative-5.jpg";
import imageCreative5 from "./images/creative-5.png";
import textureCreative6 from "./textures/creative-6.jpg";
import imageCreative6 from "./images/creative-6.png";
import textureCreative7 from "./textures/creative-7.jpg";
import imageCreative7 from "./images/creative-7.png";
import textureCreative8 from "./textures/creative-8.jpg";
import imageCreative8 from "./images/creative-8.png";
import textureCreative9 from "./textures/creative-9.jpg";
import imageCreative9 from "./images/creative-9.png";
import type { OrbShaderProps } from "./OrbShader";
import Image, { type StaticImageData } from "next/image";
import dynamic from "next/dynamic";
import { AnimatePresence, useInView } from "motion/react";
import * as m from "motion/react-m";

const OrbShader = dynamic(() => import("./OrbShader").then(mod => mod.OrbShader));

interface OrbTexture {
  texture: string;
  image: string | StaticImageData;
  placeholder?: string;
  saturation?: number;
  noiseOpacity?: number;
}

const TEXTURES = {
  ["agents-1"]: {
    texture: textureAgents1.src,
    image: imageAgents1,
    saturation: 1,
    noiseOpacity: 0.5,
    placeholder:
      "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAgAAAAICAIAAABLbSncAAAA00lEQVR42gHIADf/Af788OwBDv4BAP8AAPz+APv7/+Xu+wYEBAHf9v7q9/4GBAMIBQADAQAFAPj6+O/8/QsBksLN5fUQ/P3+DAT1DgLuCvvfBP/zHx84AWCfrfL6FO/kzQH01wD03QD++PwGHBseNAGw2erZ5PDGwqX04cUA+/v7/gX3+wMJChABwuf3BP7vubul5NzP6urt7O33x9fCRUBwAYKlsCce+//83Nfb1t3j+NHU4RQGGkpPXAGwwcHa3MkiKRsDBg33+Qnf2t8UCw0nJiufFm95mfw9JgAAAABJRU5ErkJggg==",
  },
  ["creative-1"]: {
    texture: textureCreative1.src,
    image: imageCreative1,
    saturation: 1.25,
    noiseOpacity: 0.5,
    placeholder:
      "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAgAAAAICAIAAABLbSncAAAAuElEQVR42gXBvW7CMBQG0PvdGztxXaFkqNq5S5+xa8dKHcpLwAALIyMSEuJFkBB/coCI2LnmHIT5CkOrpHs7Giqn1r5KbpziulxnoZvSEVVR2U6sMpcx4r7ZJut6MTeSnphSdPdWYkSYzFAK+yozDzFR/xCj8lbj/PtPRYI37AUvxI2Tj3f2NU4/32TAI4PacONQ+tyxhh3C9E8PB4LCO+IiXyPZc/EJtIsxadZwoUdHnOCT+fJcmyddTEwcOhaLVQAAAABJRU5ErkJggg==",
  },
  ["creative-2"]: {
    texture: textureCreative2.src,
    image: imageCreative2,
    saturation: 1,
    noiseOpacity: 0.5,
    placeholder:
      "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAgAAAAHCAIAAAC6O5sJAAAAm0lEQVR42gXByQ3CMBAAwF0fScghhU/EE0qgKf40RF10EKE8LMdgx8d6mUH7egAq0I0YBU4jdj34ZN5BPu83yRHRMxzIP2CPkKrPgnRj1pi2xOaoxldjyZru5MV0HeSknCl1B3bIe8du5FhEzakdJmzmWGcqC+ULxYW+gyqWNnfOuu9AitIqlFgrhZNaP9Rqb4OAzCEkj21mBtZ/RVBdn8qkilAAAAAASUVORK5CYII=",
  },
  ["creative-3"]: {
    texture: textureCreative3.src,
    image: imageCreative3,
    saturation: 1,
    noiseOpacity: 0.5,
    placeholder:
      "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAgAAAAICAIAAABLbSncAAAAsElEQVR42gXBO27CQBQF0HfffG15IsBSkJImTYqU6dKli5TtJMtgmyBBAaIxeMYeX87BsvvG+wdf3iS18JHwwiB8UolRNMpMlMoyYwa0gU9Wtr00CVl5y+BCDdUjd06l7+hbDoqz1qMfjvFyivuDU4mtiCCTdzONvhRfC8ZsFJeCYeSYZVpAWq0WywRn/j+/HKtWIzSmCS64efV87bbm7+fXqrOrIOtO1kk3vWxes0sPj6VHNlANDN8AAAAASUVORK5CYII=",
  },
  ["creative-4"]: {
    texture: textureCreative4.src,
    image: imageCreative4,
    saturation: 1,
    noiseOpacity: 0.5,
    placeholder:
      "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAgAAAAICAIAAABLbSncAAAAy0lEQVR42gXBwS7EUBQA0Pvubd9UtSpFmARBYuMDbG18BVsSY0l8lI0/sZPYWEmIViedad7M7evc3s45wevbRxCVYN/n9Vfx//dTzrbi5Hy8S7cPL1EiqgUvy7qZFQ1XizYaVkG2vw3quFIW9SIZDqrdtHEYGG9MN1Bsws3Mji42hutUL63Q3eMkBFbVrnXWu8PenZE/CJWeJ/fWjlARe7XCO+rG4BIierq5ytMcVDNY5cCpzPekAozRtHXOnyfyfdr/HhMfoY+gA8Q1nbtj9gWKHmMAAAAASUVORK5CYII=",
  },
  ["creative-5"]: {
    texture: textureCreative5.src,
    image: imageCreative5,
    saturation: 1,
    noiseOpacity: 0.5,
    placeholder:
      "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAgAAAAICAIAAABLbSncAAAAvklEQVR42gXBvU7CYBgF4HO+trSFD5FSJUTj4BXovWriIGripKObowlO3AGsJAz8JEBJStO+h+fh1/tifSirqoYkIwytdtLxCb/H8+WuqmtRlEGNHBnFIT+ep5vCQZKcGqBBHMp3ybenv/IUmWhGGAJo4DHKA04+fzZl+3gKq5qBlKc2utTNdcridzxb5dtjBGO/pdtMvV4TD++o/5f9MlntOt1YWe5Cb7i40vCRNnnltlAJ9j0GqRzhH5DdnwFDpVokwyygSAAAAABJRU5ErkJggg==",
  },
  ["creative-6"]: {
    texture: textureCreative6.src,
    image: imageCreative6,
    saturation: 1,
    noiseOpacity: 0.5,
    placeholder:
      "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAgAAAAICAIAAABLbSncAAAAmklEQVR42gXB2w7BQBQF0L3PGVQRIsS7//8QTz7BGwmhdQnpTGe6rcXhcmBzVPygDCiJlH4vPE+G/gERNgcryJUTkGhujK24JHfkhlozTZggjkxhS1/LatlcvpAtVWbs3TDdY7xCqGQjWAAdViF5wHij2MkKGIEC9eo7fX4BXsNroCUy81f5q+aqexsAwCdEZn4jNmzPvN+Y/Q9a0VAFCbwaQQAAAABJRU5ErkJggg==",
  },
  ["creative-7"]: {
    texture: textureCreative7.src,
    image: imageCreative7,
    saturation: 1,
    noiseOpacity: 0.5,
    placeholder:
      "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAgAAAAICAIAAABLbSncAAAAu0lEQVR42gXBvU0DQRAF4H3zdj27tyY6SyBRCiKCnIgQiZQIiRYIKIAAiT6ogCoISCEADp38f/bN+Pvw9XyfsrBORStylVy9mRrBu4vL3bB2cyCCiqiBEcx4f3qd7L9FTDWVkrWWVFTbI7w8vh37T7KlBKNAE3KS2azw5OxWJA9B+1G7Mf3tYz+IG3l6fmMuW5n0Fjvjv3FlIDOvrh8+5wvz0Dl+DXMPm5BMSmybZojpY7MiPLghoBWWkQdZu0S05FvDxgAAAABJRU5ErkJggg==",
  },
  ["creative-8"]: {
    texture: textureCreative8.src,
    image: imageCreative8,
    saturation: 1,
    noiseOpacity: 0.5,
    placeholder:
      "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAgAAAAICAIAAABLbSncAAAAnUlEQVR42gXBwXHCQBAEwJm73bNUspEjcwSOgIIHGZADX2LjyRsEVUjcaZZuXra7cZ3G/EqFMMINm46bPvXt2WFmDiYwJZJcpKnxtv/vfTUnEkEEgRATeDvucryhhhCgoDJVWE2/Q12/UN9QI4MGJZlmTqfDDA+IUM5yU0Et3WTffnX8LBjcWvFKKg93jHfG+Q9dC/YkkANlieGBgg9BdUWvwq9gNgAAAABJRU5ErkJggg==",
  },
  ["creative-9"]: {
    texture: textureCreative9.src,
    image: imageCreative9,
    saturation: 1,
    noiseOpacity: 0.5,
    placeholder:
      "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAgAAAAICAIAAABLbSncAAAAsklEQVR42hXCMU7DQBAF0P/HO15kmyhIcUGBxDUpQOIUdIiCkgOARAOHyCEoiCIl2aw9u5Pk6dFfPjY6puFau8ZVEsIuixmZ37//rkaP2qrXRvbUnSvmwv+v9abtEIQCAw0QeFuM0+96K/HAUCmAN9XUcjtP9M8fC8NeYqGw1mY6nmtKrK9vvLl1xgJBMcmJ6Uhk+vOj390zLmEz5gmlooevOvrTg68G9AuQlzFgXGLRnwDvm1Eywp7JDQAAAABJRU5ErkJggg==",
  },
} satisfies Record<string, OrbTexture>;

export type OrbTextureName = keyof typeof TEXTURES;
export const ORB_TEXTURE_NAMES = Object.keys(TEXTURES) as OrbTextureName[];

export interface OrbProps extends Omit<OrbShaderProps, "texture"> {
  texture: OrbTextureName | OrbTexture;
  placeholder?: boolean;
  placeholderBlur?: number;
  image?: { sizes: string };
  forceShaderRendering?: boolean;
}

export function Orb({
  texture: textureProp,
  placeholder = true,
  placeholderBlur,
  image,
  animated = true,
  fadeInDuration = image ? 0 : 1,
  forceShaderRendering = false,
  ...orbShaderProps
}: OrbProps) {
  const texture = typeof textureProp === "string" ? TEXTURES[textureProp] : textureProp;
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref);
  const noiseOpacity = texture.noiseOpacity ?? 0.5;

  return (
    <div ref={ref} className='tw-absolute tw-inset-0'>
      {placeholder && texture.placeholder && (
        <OrbPlaceholder
          src={texture.placeholder}
          blur={placeholderBlur}
          className='tw-absolute tw-inset-0 tw-size-full'
        />
      )}
      {image && <Image src={texture.image} alt='' className='tw-object-cover' sizes={image.sizes} fill />}
      <AnimatePresence initial={false}>
        {(animated || forceShaderRendering) && inView && (
          <m.div
            className='tw-absolute tw-inset-0'
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.3 } }}
          >
            <OrbShader
              texture={texture.texture}
              saturation={texture.saturation}
              fadeInDuration={forceShaderRendering ? 0 : fadeInDuration}
              animated={forceShaderRendering ? animated : undefined}
              {...orbShaderProps}
            />
          </m.div>
        )}
      </AnimatePresence>
      {noiseOpacity > 0 && (
        <div className='tw-absolute tw-inset-0 tw-mix-blend-overlay'>
          <Noise style={{ opacity: noiseOpacity }} />
        </div>
      )}
    </div>
  );
}

function OrbPlaceholder({
  src,
  blur = 8,
  ...props
}: React.ComponentPropsWithoutRef<"svg"> & { src: string; blur?: number }) {
  const id = useId();

  return (
    <svg aria-hidden {...props}>
      <filter id={id} colorInterpolationFilters='sRGB'>
        <feGaussianBlur stdDeviation={blur} />
        <feColorMatrix values='1 0 0 0 0 0 1 0 0 0 0 0 1 0 0 0 0 0 100 -1' result='s' />
        <feFlood x='0' y='0' width='100%' height='100%' />
        <feComposite operator='out' in='s' />
        <feComposite in2='SourceGraphic' />
        <feGaussianBlur stdDeviation={blur} />
      </filter>
      <image
        width='100%'
        height='100%'
        preserveAspectRatio='xMidYMid slice'
        style={{ filter: `url(#${id})` }}
        href={src}
      />
    </svg>
  );
}
